import { AxiosError, AxiosResponse } from 'axios';
import { Task } from 'redux-saga';
import {
    call,
    cancel,
    delay,
    fork,
    put,
    takeEvery,
    takeLatest,
} from 'redux-saga/effects';

import {
    deleteRecordingUrl,
    deleteSeriesRecordingUrl,
    getRecordingsQuotaUsageUrl,
    getRecordingsUrl,
    getSeriesRecordingsUrl,
    protectUnprotectRecordingUrl,
    scheduleRecordingUrl,
    scheduleSeriesRecordingUrl,
} from 'config/api';

import { AsyncActionMetaOptions } from 'store/types';
import { delayRetryRequest } from 'store/utils.ts';

import { isErrorNeedsRetry } from 'services/opf-retry-mechanism/utils.ts';

import NagraInstance from 'utils/axios/nagra/DefaultInstance';
import isFunction from 'utils/function';

import {
    cancelRecordingFailure,
    cancelRecordingSuccess,
    cancelSeriesRecordingFailure,
    cancelSeriesRecordingSuccess,
    deleteRecording,
    deleteRecordingFailure,
    deleteRecordingSuccess,
    deleteSeriesRecording,
    deleteSeriesRecordingFailure,
    deleteSeriesRecordingSuccess,
    getQuotaUsage,
    getQuotaUsageFailure,
    getQuotaUsageSuccess,
    getRecordingsCache,
    getRecordingsCacheFailure,
    getRecordingsCacheRequest,
    getRecordingsCacheSuceess,
    protectRecording,
    protectRecordingFailure,
    protectRecordingSuccess,
    scheduleRecording,
    scheduleRecordingFailure,
    scheduleRecordingSuccess,
    scheduleSeriesRecording,
    scheduleSeriesRecordingFailure,
    scheduleSeriesRecordingSuccess,
    unprotectRecording,
    unprotectRecordingFailure,
    unprotectRecordingSuccess,
} from './actions';
import { RecordingActionType } from './actions.types';
import {
    CANCEL_RECORDING,
    CANCEL_SERIES_RECORDING,
    DELETE_RECORDING,
    DELETE_SERIES_RECORDING,
    GET_QUOTA_USAGE,
    GET_RECORDINGS_CACHE,
    PROTECT_RECORDING,
    SCHEDULE_RECORDING,
    SCHEDULE_SERIES_RECORDING,
    UNFOCUS_RECORDING,
    UNPROTECT_RECORDING,
} from './constants';
import {
    CachedRecordingsResponse,
    QuotaUsage,
    RecordingError,
    SeriesRecordingResponse,
} from './reducer.types';

function* handleUnfocusRecording(
    setUnfocusCalled: () => void,
    timeoutTask: Task<any>,
) {
    setUnfocusCalled();
    yield cancel(timeoutTask);
}

function* watchUnfocusRecording(
    setUnfocusCalled: () => void,
    timeoutTask: Task<any>,
) {
    yield takeEvery(
        UNFOCUS_RECORDING,
        handleUnfocusRecording,
        setUnfocusCalled,
        timeoutTask,
    );
}

function* onGetQuotaUsage({
    meta: { onSuccess, onFailure },
}: ReturnType<typeof getQuotaUsage>) {
    const requestUrl = getRecordingsQuotaUsageUrl();

    try {
        const { data }: AxiosResponse<QuotaUsage> = yield call(
            NagraInstance.get,
            requestUrl,
        );

        yield put(getQuotaUsageSuccess(data));

        if (isFunction(onSuccess)) {
            yield call(onSuccess, data);
        }
    } catch (error: unknown) {
        yield put(getQuotaUsageFailure(error as RecordingError));

        if (isFunction(onFailure)) {
            yield call(onFailure, error as RecordingError);
        }
    }
}

function* onTimeoutRecordingAction({ onTimeout }: AsyncActionMetaOptions) {
    yield delay(5000);
    if (isFunction(onTimeout)) {
        yield call(onTimeout);
    }
}

function* onScheduleRecording({
    payload,
    meta: { onSuccess, onFailure, onTimeout },
}: ReturnType<typeof scheduleRecording>) {
    const requestUrl = scheduleRecordingUrl();

    let unfocusCalled = false;
    const setUnfocusCalled = () => {
        unfocusCalled = true;
    };

    try {
        const timeoutTask: Task = yield fork(onTimeoutRecordingAction, {
            onTimeout,
        });

        yield fork(watchUnfocusRecording, setUnfocusCalled, timeoutTask);

        yield call(NagraInstance.post, requestUrl, payload);
        yield cancel(timeoutTask);

        yield put(scheduleRecordingSuccess());

        if (isFunction(onSuccess) && !unfocusCalled) {
            yield call(onSuccess);
        }
    } catch (error: unknown) {
        yield put(scheduleRecordingFailure(error as RecordingError));

        if (isFunction(onFailure)) {
            yield call(onFailure, error as RecordingError);
        }
    }
}

function* onProtectRecording({
    payload: { recordingId },
    meta: { onSuccess, onFailure, onTimeout },
}: ReturnType<typeof protectRecording>) {
    const requestUrl = protectUnprotectRecordingUrl(recordingId);

    let unfocusCalled = false;
    const setUnfocusCalled = () => {
        unfocusCalled = true;
    };

    const requestPayload = {
        isProtected: true,
    };

    try {
        const timeoutTask: Task = yield fork(onTimeoutRecordingAction, {
            onTimeout,
        });

        yield fork(watchUnfocusRecording, setUnfocusCalled, timeoutTask);

        yield call(NagraInstance.patch, requestUrl, requestPayload);
        yield cancel(timeoutTask);

        yield put(protectRecordingSuccess());

        if (isFunction(onSuccess) && !unfocusCalled) {
            yield call(onSuccess);
        }
    } catch (error: unknown) {
        yield put(protectRecordingFailure(error as RecordingError));

        if (isFunction(onFailure)) {
            yield call(onFailure, error as RecordingError);
        }
    }
}

function* onUnprotectRecording({
    payload: { recordingId, pinType, pin },
    meta: { onSuccess, onFailure, onTimeout },
}: ReturnType<typeof unprotectRecording>) {
    const requestUrl = protectUnprotectRecordingUrl(recordingId);

    let unfocusCalled = false;
    const setUnfocusCalled = () => {
        unfocusCalled = true;
    };

    const requestPayload = {
        isProtected: false,
        pin: {
            type: pinType,
            value: pin,
        },
    };

    try {
        const timeoutTask: Task = yield fork(onTimeoutRecordingAction, {
            onTimeout,
        });

        yield fork(watchUnfocusRecording, setUnfocusCalled, timeoutTask);

        yield call(NagraInstance.patch, requestUrl, requestPayload);
        yield cancel(timeoutTask);

        yield put(unprotectRecordingSuccess());

        if (isFunction(onSuccess) && !unfocusCalled) {
            yield call(onSuccess);
        }
    } catch (error: unknown) {
        yield put(unprotectRecordingFailure(error as RecordingError));

        if (isFunction(onFailure)) {
            yield call(onFailure, error as RecordingError);
        }
    }
}

function* onDeleteRecording({
    payload: { recordingId, pinType, pin, actionType },
    meta: { onSuccess, onFailure, onTimeout },
}: ReturnType<typeof deleteRecording>) {
    const requestUrl = deleteRecordingUrl(recordingId, actionType);

    let unfocusCalled = false;
    const setUnfocusCalled = () => {
        unfocusCalled = true;
    };

    const requestPayload = pin
        ? {
              type: pinType,
              value: pin,
          }
        : undefined;

    try {
        const timeoutTask: Task = yield fork(onTimeoutRecordingAction, {
            onTimeout,
        });

        yield fork(watchUnfocusRecording, setUnfocusCalled, timeoutTask);

        yield call(NagraInstance.post, requestUrl, requestPayload);

        yield put(
            actionType === RecordingActionType.CANCEL
                ? cancelRecordingSuccess()
                : deleteRecordingSuccess(),
        );

        yield cancel(timeoutTask);

        if (isFunction(onSuccess) && !unfocusCalled) {
            yield call(onSuccess);
        }
    } catch (error: unknown) {
        if (actionType === RecordingActionType.CANCEL) {
            yield put(cancelRecordingFailure(error as RecordingError));
        } else {
            yield put(deleteRecordingFailure(error as RecordingError));
        }

        if (isFunction(onFailure)) {
            yield call(onFailure, error as RecordingError);
        }
    }
}

function* onScheduleSeriesRecording({
    payload,
    meta: { onSuccess, onFailure, onTimeout },
}: ReturnType<typeof scheduleSeriesRecording>) {
    const requestUrl = scheduleSeriesRecordingUrl();

    let unfocusCalled = false;
    const setUnfocusCalled = () => {
        unfocusCalled = true;
    };
    const requestPayload = {
        ...payload,
        seriesId: undefined,
    };

    let timeoutTask: Task | undefined;

    try {
        timeoutTask = yield fork(onTimeoutRecordingAction, {
            onTimeout,
        });
        yield fork(watchUnfocusRecording, setUnfocusCalled, timeoutTask!);

        yield call(NagraInstance.post, requestUrl, requestPayload);

        yield put(scheduleSeriesRecordingSuccess());
        yield cancel(timeoutTask!);

        if (isFunction(onSuccess) && !unfocusCalled) {
            yield call(onSuccess);
        }
    } catch (error: unknown) {
        if (timeoutTask) {
            yield cancel(timeoutTask);
        }

        yield put(scheduleSeriesRecordingFailure(error as RecordingError));

        if (isFunction(onFailure)) {
            yield call(onFailure, error as RecordingError);
        }
    }
}

function* onDeleteSeriesRecording({
    payload: {
        seriesRecordingId,
        deleteRecordingOption,
        actionType,
        pinType,
        pin,
    },
    meta: { onSuccess, onFailure, onTimeout },
}: ReturnType<typeof deleteSeriesRecording>) {
    const requestUrl = deleteSeriesRecordingUrl(
        seriesRecordingId,
        deleteRecordingOption,
        actionType,
    );

    const requestPayload = pin
        ? {
              type: pinType,
              value: pin,
          }
        : undefined;

    let unfocusCalled = false;
    const setUnfocusCalled = () => {
        unfocusCalled = true;
    };

    try {
        const timeoutTask: Task = yield fork(onTimeoutRecordingAction, {
            onTimeout,
        });

        yield fork(watchUnfocusRecording, setUnfocusCalled, timeoutTask);

        yield call(NagraInstance.post, requestUrl, requestPayload);

        yield put(
            actionType === RecordingActionType.CANCEL
                ? cancelSeriesRecordingSuccess()
                : deleteSeriesRecordingSuccess(),
        );

        yield cancel(timeoutTask);

        if (isFunction(onSuccess) && !unfocusCalled) {
            yield call(onSuccess);
        }
    } catch (error: unknown) {
        yield put(
            actionType === RecordingActionType.CANCEL
                ? cancelSeriesRecordingFailure(error as RecordingError)
                : deleteSeriesRecordingFailure(error as RecordingError),
        );

        if (isFunction(onFailure)) {
            yield call(onFailure, error as RecordingError);
        }
    }
}

function* requestRecordingsCache(
    action: ReturnType<typeof getRecordingsCache>,
) {
    const {
        meta: { onSuccess },
    } = action;
    const [recordingsCacheResponse, seriesRecordingsStatusCacheResponse]: [
        PromiseAllSettledConfig<
            AxiosResponse<CachedRecordingsResponse>,
            AxiosError
        >,
        PromiseAllSettledConfig<
            AxiosResponse<SeriesRecordingResponse>,
            AxiosError
        >,
    ] = yield call(
        [Promise, 'allSettled'],
        [
            NagraInstance.get(
                getRecordingsUrl({
                    fields: [
                        'id',
                        'seriesId',
                        'seriesRecordingId',
                        'status',
                        'eventId',
                        'isProtected',
                        'captureEnd',
                        'captureDuration',
                    ],
                    limit: 1000000,
                }),
            ),
            NagraInstance.get(getSeriesRecordingsUrl()),
        ],
    );

    const recordingsCache =
        recordingsCacheResponse.status === 'fulfilled'
            ? recordingsCacheResponse.value.data
            : undefined;
    const seriesRecordingsStatusCache =
        seriesRecordingsStatusCacheResponse.status === 'fulfilled'
            ? seriesRecordingsStatusCacheResponse.value.data
            : undefined;

    if (recordingsCache && seriesRecordingsStatusCache) {
        yield put(
            getRecordingsCacheSuceess({
                timestamp: Date.now(),
                recordings: recordingsCache.recordingSet,
                series: seriesRecordingsStatusCache.series,
            }),
        );

        if (isFunction(onSuccess)) {
            yield call(onSuccess);
        }
    } else if (
        recordingsCacheResponse.status === 'rejected' &&
        recordingsCacheResponse.reason
    ) {
        throw recordingsCacheResponse.reason;
    } else if (
        seriesRecordingsStatusCacheResponse.status === 'rejected' &&
        seriesRecordingsStatusCacheResponse.reason
    ) {
        throw seriesRecordingsStatusCacheResponse.reason;
    } else {
        throw new Error('Cached recordings data loading failed.');
    }
}

function* onGetRecordingsCache(action: ReturnType<typeof getRecordingsCache>) {
    const {
        meta: { onFailure, autoRetry },
    } = action;

    try {
        yield put(getRecordingsCacheRequest());
        yield call(requestRecordingsCache, action);
    } catch (error: any) {
        if (autoRetry && isErrorNeedsRetry(error)) {
            yield fork(retryOnGetRecordingsCache, action);
        }

        yield put(getRecordingsCacheFailure(error));

        if (isFunction(onFailure)) {
            yield call(onFailure, error);
        }
    }
}

function* retryOnGetRecordingsCache(
    action: ReturnType<typeof getRecordingsCache>,
) {
    const {
        onFailure,
        retryCount = 5,
        retryDuration = 5 * 60 * 1000,
    } = action.meta;

    for (let i = 1; i <= retryCount; i += 1) {
        yield call(delayRetryRequest, i, retryCount, retryDuration);

        try {
            yield call(requestRecordingsCache, action);
            return;
        } catch (error: any) {
            if (i === retryCount) {
                yield put(getRecordingsCacheFailure(error));

                if (isFunction(onFailure)) {
                    yield call(onFailure, error);
                }
            }
        }
    }
}

export default [
    // SINGLE RECORDING HANDLERS
    takeLatest(GET_QUOTA_USAGE, onGetQuotaUsage),
    takeLatest(SCHEDULE_RECORDING, onScheduleRecording),
    takeLatest(PROTECT_RECORDING, onProtectRecording),
    takeLatest(UNPROTECT_RECORDING, onUnprotectRecording),
    takeLatest(DELETE_RECORDING, onDeleteRecording),
    takeLatest(CANCEL_RECORDING, onDeleteRecording),
    // SERIES RECORDINGS HANDLERS
    takeLatest(SCHEDULE_SERIES_RECORDING, onScheduleSeriesRecording),
    takeLatest(DELETE_SERIES_RECORDING, onDeleteSeriesRecording),
    takeLatest(CANCEL_SERIES_RECORDING, onDeleteSeriesRecording),
    takeLatest(GET_RECORDINGS_CACHE, onGetRecordingsCache),
];
